import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { operatorApiFetch } from '@services/operator/apiService';
import '../FulfillmentDashboard/FulfillmentWall.css';
import './CarrierWall.css';

const POLL_INTERVAL = 60_000;
const PAGE_INTERVAL = 8_000; // 每頁停留 8 秒
const ROWS_PER_PAGE = 8;

const getPctLevel = (pct) => {
  if (pct >= 90) return 'high';
  if (pct >= 50) return 'mid';
  return 'low';
};

const CarrierWall = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [page, setPage] = useState(0);
  const containerRef = useRef(null);
  const wakeLockRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await operatorApiFetch('fulfillment_dashboard/carrier_progress');
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
        setStale(false);
      } else {
        setStale(true);
      }
    } catch {
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Wake Lock
  useEffect(() => {
    const req = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    req();
    const onVis = () => { if (document.visibilityState === 'visible') req(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      wakeLockRef.current?.release();
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // 按總數排序（多的在前）
  const sortedCarriers = useMemo(() => {
    if (!data?.carriers) return [];
    return [...data.carriers]
      .map((c) => {
        const total = c.shipped + c.processing;
        const pct = total > 0 ? Math.round((c.shipped / total) * 100) : 0;
        return { ...c, total, pct };
      })
      .sort((a, b) => b.total - a.total);
  }, [data?.carriers]);

  // 分頁
  const totalPages = Math.max(Math.ceil(sortedCarriers.length / ROWS_PER_PAGE), 1);
  const currentPage = useMemo(() => page % totalPages, [page, totalPages]);
  const pageCarriers = useMemo(
    () => sortedCarriers.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE),
    [sortedCarriers, currentPage],
  );

  // 自動翻頁（只有超過一頁時才啟動）
  useEffect(() => {
    if (totalPages <= 1) return;
    const timer = setInterval(() => setPage((p) => p + 1), PAGE_INTERVAL);
    return () => clearInterval(timer);
  }, [totalPages]);

  const summaryTotal = useMemo(() => {
    if (!data?.summary) return { shipped: 0, processing: 0, total: 0, pct: 0 };
    const shipped = data.summary.shipped || 0;
    const processing = data.summary.processing || 0;
    const total = shipped + processing;
    const pct = total > 0 ? Math.round((shipped / total) * 100) : 0;
    return { shipped, processing, total, pct };
  }, [data?.summary]);

  // Clock display
  const timeStr = clock.toLocaleTimeString('zh-TW', { hour12: false });
  const dateStr = `${String(clock.getMonth() + 1).padStart(2, '0')}/${String(clock.getDate()).padStart(2, '0')}`;

  if (loading && !data) {
    return (
      <div className="fw-loading">
        <div className="fw-loading-spinner" />
        <div className="fw-loading-text">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="fw cw" ref={containerRef}>
      <div className="fw-header">
        <div className="fw-header-left">
          <span className="fw-header-logo">GoWarehouse</span>
          <span className="fw-header-sep" />
          <span className="fw-header-title">{t('operator.carrier_dashboard.title')}</span>
          <span className="cw-zone-badge">{t('operator.carrier_dashboard.zone')}</span>
        </div>
        <div className="fw-header-right">
          <span className="fw-clock">{timeStr}</span>
          <span className="fw-date">{dateStr}</span>
        </div>
      </div>

      <div className="cw-summary">
        <div className="cw-summary-item">
          {t('operator.carrier_dashboard.total_shipped')}
          <strong>{summaryTotal.shipped}</strong>
          <span className="cw-summary-slash">/</span>
          <strong>{summaryTotal.total}</strong>
        </div>
        <div className="cw-summary-item">
          {t('operator.carrier_dashboard.total_processing')}
          <strong>{summaryTotal.processing}</strong>
        </div>
        <span className="cw-summary-pct">{summaryTotal.pct}%</span>
        {totalPages > 1 && (
          <span className="cw-page-indicator">{currentPage + 1} / {totalPages}</span>
        )}
      </div>

      {sortedCarriers.length > 0 ? (
        <div className="cw-list" key={currentPage}>
          {pageCarriers.map((c) => {
            const level = getPctLevel(c.pct);
            const barWidth = c.total > 0 ? (c.shipped / c.total) * 100 : 0;
            return (
              <div key={c.carrier} className="cw-row">
                <div className="cw-carrier-name">
                  {t(`operator.carrier_dashboard.carriers.${c.carrier}`, c.carrier)}
                </div>
                <div className="cw-bar-track">
                  <div
                    className={`cw-bar-fill cw-bar-fill--${level}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <div className="cw-numbers">
                  {c.shipped}
                  <span className="cw-numbers-slash">/</span>
                  {c.total}
                </div>
                <div className={`cw-pct cw-pct--${level}`}>
                  {c.pct}%
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cw-empty">{t('operator.carrier_dashboard.no_data')}</div>
      )}

      <div className="fw-footer">
        <div className="fw-footer-info">
          <span>
            {t('operator.carrier_dashboard.last_updated')}{' '}
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleTimeString('zh-TW', { hour12: false })
              : '--:--:--'}
          </span>
          <span className="fw-footer-sep">|</span>
          <span>{t('operator.carrier_dashboard.polling_interval', { seconds: POLL_INTERVAL / 1000 })}</span>
          {stale && <span className="fw-stale">{t('operator.carrier_dashboard.data_stale')}</span>}
        </div>
        <button className="fw-fs-btn" onClick={toggleFullscreen}>
          {t('operator.carrier_dashboard.fullscreen')}
        </button>
      </div>
    </div>
  );
};

export default CarrierWall;
